import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Delete, Edit, Add } from "@mui/icons-material";
import { useCookies } from "react-cookie";


const KASSE_ENDPOINT = 'https://fkcrenezvf.execute-api.eu-central-1.amazonaws.com/Prod';

export default function CashRegisterProducts() {
  const [products, setProducts] = useState(null);
  const [originalProducts, setOriginalProducts] = useState(null);
  const [vatDefinitions, setVatDefinitions] = useState([]);
  const [purchaserAgencies, setPurchaserAgencies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editProduct, setEditProduct] = useState(null);

  const [cookies] = useCookies(["crKey"])

  const authHeaders = {
  headers: {
    Authorization: `Bearer ${cookies.crKey} y 1 z`,
  },
};
  const hasUnsavedChanges = JSON.stringify(products) !== JSON.stringify(originalProducts);

  useEffect(() => {
    axios.get(KASSE_ENDPOINT + "/products", authHeaders).then((res) => {
      const { products, vat_definitions, purchaser_agencies } = res.data || {};
      setProducts(products || []);
      setOriginalProducts(products || []); // Save initial copy
      setVatDefinitions(vat_definitions || []);
      setPurchaserAgencies(purchaser_agencies || []);

    });
  }, []);

  const handleEdit = (index) => {
    setEditIndex(index);
    setEditProduct({ ...products[index] });
  };

  const handleEditChange = (field, value) => {
    setEditProduct({
      ...editProduct,
      [field]: field === "price" ? parseFloat(value) : value,
    });
  };

  const saveEdit = () => {
    const updated = [...products];
    updated[editIndex] = editProduct;
    setProducts(updated);
    setEditIndex(null);
    setEditProduct(null);

  };

  const cancelEdit = () => {
    setEditIndex(null);
    setEditProduct(null);
  };

  const addProduct = () => {
    const newProduct = {
      name: "",
      price: 0,
      sku: "",
      vat_definition_export_id: vatDefinitions[0]?.vat_definition_export_id || 1,
      purchaser_agency: ""
    };
    const updated = [...products, newProduct];
    setProducts(updated);
    setEditIndex(updated.length - 1);
    setEditProduct(newProduct);
  };

  const removeProduct = (index) => {
    const updated = [...products];
    updated.splice(index, 1);
    setProducts(updated);
  };

  const moveProduct = (index, direction) => {
    const updated = [...products];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= products.length) return;
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    setProducts(updated);
  };

  const isProductValid = (p) => {

    if (!p) return false;
    if (!p.name) return false;
    return p.name.trim() !== "" && !isNaN(p.price) && p.sku.trim() !== "" && p.vat_definition_export_id;
  }

  const allValid = Array.isArray(products) && products.every(isProductValid);


  const save = async () => {
    if (!allValid) {
      alert("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(KASSE_ENDPOINT + "/products", products, authHeaders);
      setOriginalProducts(products); // Reset original after successful save
      alert("Gespeichert");
    } catch {
      alert("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (products === null) return;

  return (
    <Box sx={{ p: 4, maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Produktkonfiguration
      </Typography>

      {products === null ? (
        <Skeleton variant="rectangular" width="100%" height="300px" />
      ) : (
        <TableContainer component={Card} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Produktname</TableCell>
                <TableCell>Preis</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>VAT</TableCell>
                <TableCell>Agentur</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.price.toFixed(2)} €</TableCell>
                  <TableCell>{p.sku}</TableCell>
                  <TableCell>{p.vat_definition_export_id}</TableCell>
                  <TableCell>
                    {p.purchaser_agency || <Typography color="text.secondary">–</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => moveProduct(i, -1)} disabled={i === 0}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => moveProduct(i, 1)} disabled={i === products.length - 1}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleEdit(i)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => removeProduct(i)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {products && (
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<Add />} onClick={addProduct}>
            Produkt hinzufügen
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !allValid}>
            Speichern
          </Button>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editIndex !== null} onClose={cancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Produkt bearbeiten</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Produktname *"
            value={editProduct?.name || ""}
            onChange={(e) => handleEditChange("name", e.target.value)}
            error={!editProduct?.name}
          />
          <TextField
            fullWidth
            label="Preis *"
            type="number"
            value={editProduct?.price || ""}
            onChange={(e) => handleEditChange("price", e.target.value)}
            error={isNaN(editProduct?.price)}
          />
          <TextField
            fullWidth
            label="SKU *"
            value={editProduct?.sku || ""}
            onChange={(e) => handleEditChange("sku", e.target.value)}
            error={!editProduct?.sku}
          />
          <TextField
            select
            fullWidth
            label="VAT Definition *"
            value={editProduct?.vat_definition_export_id || ""}
            onChange={(e) => handleEditChange("vat_definition_export_id", parseInt(e.target.value))}
            error={!editProduct?.vat_definition_export_id}
          >
            {vatDefinitions.map((v) => (
              <MenuItem key={v.vat_definition_export_id} value={v.vat_definition_export_id}>
                {v.vat_definition_export_id} - {v.percentage}% ({v.description})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Agentur (optional)"
            value={editProduct?.purchaser_agency || ""}
            onChange={(e) => handleEditChange("purchaser_agency", e.target.value)}
          >
            <MenuItem value="">- keine -</MenuItem>
            {purchaserAgencies.map((a) => (
              <MenuItem key={a.purchaser_agency_id} value={a.purchaser_agency_id}>
                {a.name} ({a.tax_number})
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelEdit}>Abbrechen</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!isProductValid(editProduct)}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
